import { z } from 'zod';

import {
  GithubIssueRelationshipsSchema,
  IssueProviderSchema,
  LinearIssueRelationSchema,
} from './issues';
import {
  KruxitAreaGroupSchema,
  KruxitRankSchema,
  PriorityBandSchema,
} from './kruxit';
import type { WorkItemsPeopleScope } from './people';
import { CommitShaSchema, PullRequestReviewerSchema } from './pull-requests';
import { WorkRelationshipSchema } from './work-relationships';
import { WorkflowStatusSchema } from './workflow-status';

// ---------------------------------------------------------------------------
// Board — a unified view spanning the user's issues (GitHub + Linear) and pull
// requests (GitHub), grouped into the analogous relationship sections of the
// per-feature views: authored (issue created + PR authored), assigned, review
// requested (PRs), reviewed (PRs), mentioned (issues). The dashboard shows
// Issues / Pull requests / All tabs; this contract is the presentation-neutral
// shape the core returns and the route renders. A BoardItem normalises an issue
// or a PR to the few fields the unified list needs.
// ---------------------------------------------------------------------------

export const BoardItemKindSchema = z.enum(['issue', 'pull-request']);
export type BoardItemKind = z.infer<typeof BoardItemKindSchema>;

export const BoardItemStateSchema = z.enum(['open', 'closed', 'merged']);
export type BoardItemState = z.infer<typeof BoardItemStateSchema>;

/**
 * The PR-only fields a unified item carries so the work-items view can nest a
 * stacked-PR forest and the MCP payload stays self-describing (an agent can
 * rebuild a stack and tell a draft from a ready PR). `null` for issues.
 */
export const BoardItemPrReferencesSchema = z.object({
  /** Target/base branch, or `""` when GitHub omitted it. */
  baseBranch: z.string(),
  /** Source/head branch, or `""`. */
  headBranch: z.string(),
  /** `owner/repo` the head branch lives in (a fork for a fork PR, else the repo). */
  headRepo: z.string(),
  /** Head-commit SHA (`headRefOid`); the scan anchor. `""` when GitHub omits it. */
  headSha: CommitShaSchema,
  /** Base-commit SHA (`baseRefOid`); the diff base for a scan. `""` when omitted. */
  baseSha: CommitShaSchema,
  /** True for a draft PR. */
  isDraft: z.boolean(),
  /** Lines added by the PR; `0` when GitHub omits it. */
  additions: z.number().int().nonnegative(),
  /** Lines removed by the PR; `0` when GitHub omits it. */
  deletions: z.number().int().nonnegative(),
  /** Files the PR changed; `0` when GitHub omits it. */
  changedFiles: z.number().int().nonnegative(),
  /** Distinct reviewers with their terminal review state; best-effort (capped). */
  reviewers: z.array(PullRequestReviewerSchema),
  /** Same-repository issue keys explicitly claimed by the PR body. */
  delivers: z.array(z.string()).optional(),
  /**
   * Changed file paths (capped) for the `kruxit-area` overlap clustering. Optional:
   * absent on issues and on snapshots from before this shipped. Used server-side to
   * cluster overlapping PRs; the client reads the resulting `areaGroup`, not this.
   */
  files: z.array(z.string()).optional(),
});
export type BoardItemPrReferences = z.infer<typeof BoardItemPrReferencesSchema>;

/** One issue or PR, normalised for the unified list. */
export const BoardItemSchema = z
  .object({
    kind: BoardItemKindSchema,
    /** Source provider: `github` (every PR + GitHub issues) or `linear` (Linear issues). */
    provider: IssueProviderSchema,
    /** Globally-unique id: an issue `sourceId`, or `<repo>#<number>` for a PR. */
    id: z.string().min(1),
    /** Short human key: `#123` (GitHub) or `ENG-45` (Linear). */
    displayKey: z.string(),
    title: z.string(),
    url: z.string(),
    /** Repo (`owner/name`) or Linear `workspace/team`. */
    scope: z.string(),
    state: BoardItemStateSchema,
    author: z.string(),
    /**
     * True when the provider reports the author as automation. GitHub issues
     * only — a PR keeps it absent on purpose, because a bot-authored PR waiting
     * on the caller's review is still their move (see `next-work-signals.ts`).
     */
    authorIsBot: z.boolean().optional(),
    /** True when GitHub reports the issue as pinned to its repository. */
    pinned: z.boolean().optional(),
    /** Assignee logins (GitHub) / display names (Linear); empty when unassigned. */
    assignees: z.array(z.string()),
    // ISO 8601 — both sources already normalise to it; enforced so the recency
    // sort in `compareBoardItems` can rely on a parseable timestamp.
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    /** Linear priority (1 urgent … 4 low); `null` for GitHub. */
    priority: z.number().nullable(),
    /** Conversation comment count (issues + PRs); `0` when none or omitted upstream. */
    comments: z.number().int().nonnegative(),
    /** True when `comments` is a capped lower bound (Linear only); UI shows "N+". */
    commentsTruncated: z.boolean().optional(),
    /** Effective labels (own labels plus inherited parent context), for issues. */
    labels: z.array(z.string()).optional(),
    /** GitHub issue relationships; absent for PRs, Linear, and older snapshots. */
    relationships: GithubIssueRelationshipsSchema.optional(),
    /** Linear-asserted issue relations; absent for GitHub and when unread. */
    relations: z.array(LinearIssueRelationSchema).optional(),
    /**
     * Normalised pipeline stage, derived at the consolidation boundary by
     * `deriveWorkflow` — the "group by workflow" projection and the row's stage
     * badge. Distinct from `state` (the binary merge state).
     */
    workflowStatus: WorkflowStatusSchema,
    /** ISO — when it entered `workflowStatus` (best-effort until the event log). */
    workflowStatusSince: z.iso.datetime(),
    /** PR-only branch refs + draft flag (for the stacked forest); `null` for issues. */
    pr: BoardItemPrReferencesSchema.nullable(),
    /**
     * Server-assigned "Kruxit ordered" ranks (one per axis) for the item-level
     * "what to work on next" orderings. Optional: a stale snapshot or a
     * relationship-only payload may omit it.
     */
    kruxitRank: KruxitRankSchema.optional(),
    /**
     * Server-derived severity band for items the source never prioritised (every
     * GitHub item; a Linear issue left at "No priority"). Deterministic — see
     * `libs/account/derived-priority.ts`. `undefined` when the item carries a real
     * priority, which always wins. Distinct from `priority`.
     */
    derivedPriority: PriorityBandSchema.optional(),
    /** Haiku topic label for the `kruxit-area` similarity clustering; `undefined` when unavailable. */
    topic: z.string().optional(),
    /** Server-computed `kruxit-area` cluster (file overlap → topic → repo); `undefined` outside that axis. */
    areaGroup: KruxitAreaGroupSchema.optional(),
    /**
     * Server-computed duplicate/overlap edges to other work, provenance-tagged
     * (see `work-relationships.ts`) and capped at `WORK_RELATIONSHIP_LIMIT`.
     * `undefined` on a snapshot cached before this shipped, and `[]` when the
     * pass ran and found nothing — the two are deliberately distinguishable.
     */
    overlaps: z.array(WorkRelationshipSchema).optional(),
  })
  // `pr` is set iff the item is a PR — issues never carry branch refs. Enforced
  // here so the route's `BoardResponseSchema.parse` rejects an inconsistent item
  // rather than letting downstream rely on defensive `pr?.` chaining.
  .refine((item) => (item.kind === 'pull-request') === (item.pr !== null), {
    message: 'pr must be set for a pull-request and null for an issue',
  });
export type BoardItem = z.infer<typeof BoardItemSchema>;

/**
 * The board's relationship sections, in default ("my work first") display order:
 * `authored` (created/authored with no assignee other than the author),
 * `delegated` (authored but assigned to someone else), `assigned` (assigned to the
 * selected people, less anything already in authored/delegated), `reviewRequested`
 * (PRs), `mentioned` (issues, less anything surfaced above), `reviewed` (PRs, last —
 * lowest immediate attention). The UI can flip to an "unblock others first" order;
 * this list stays the canonical order for the MCP surface.
 */
export const BoardBucketKeys = [
  'authored',
  'delegated',
  'assigned',
  'reviewRequested',
  'mentioned',
  'reviewed',
  'recentlyMerged',
] as const;
export const BoardBucketKeySchema = z.enum(BoardBucketKeys);
export type BoardBucketKey = z.infer<typeof BoardBucketKeySchema>;

export const BoardBucketsSchema = z.object({
  authored: z.array(BoardItemSchema),
  delegated: z.array(BoardItemSchema),
  assigned: z.array(BoardItemSchema),
  reviewRequested: z.array(BoardItemSchema),
  mentioned: z.array(BoardItemSchema),
  reviewed: z.array(BoardItemSchema),
  // PRs the selected people merged in the recent window (see RECENTLY_MERGED_WINDOW_MS).
  // A time-bounded "work done" slice that the open-only default never fetches, so
  // it's its own board bucket rather than a relationship the cascade would dedupe.
  recentlyMerged: z.array(BoardItemSchema),
});
export type BoardBuckets = z.infer<typeof BoardBucketsSchema>;

/**
 * A mapped GitHub-org ↔ Linear-workspace pair, so the scope filter can fuse the
 * org's repos and its Linear workspace into one `githubOrg/linearWorkspace` group.
 * `githubOrg` is the org login (a GitHub scope's `owner`); `linearWorkspace` is the
 * workspace's display label (a Linear scope's prefix).
 */
export const OrgGroupSchema = z.object({
  githubOrg: z.string().min(1),
  linearWorkspace: z.string().min(1),
});
export type OrgGroup = z.infer<typeof OrgGroupSchema>;

/**
 * Why a board is `partial`, so the surfaces can tell an actionable failure from a
 * transient one. `auth` means a GitHub 401 — the user's PAT is expired/revoked and
 * must be reconnected; `transient` is everything else (rate limit, network, 5xx),
 * which usually clears on its own. `null` when the board isn't partial.
 */
export const PartialReasonSchema = z.enum(['auth', 'transient']);
export type PartialReason = z.infer<typeof PartialReasonSchema>;

/** Response envelope for `GET /api/account/work-items`. */
export const BoardResponseSchema = z.object({
  /** True when at least one provider is connected. */
  connected: z.boolean(),
  /** True when a provider/bucket failed (the lists may be incomplete). */
  partial: z.boolean(),
  /**
   * Why it's partial (`auth` ⇒ reconnect the PAT); `null` when not partial.
   * Defaults to `null` so a board snapshot cached before this field existed still
   * parses (a graceful cache transition) instead of being dropped as malformed.
   */
  partialReason: PartialReasonSchema.nullable().default(null),
  buckets: BoardBucketsSchema,
  /** Mapped GitHub-org ↔ Linear-workspace pairs, for the merged scope filter. */
  orgGroups: z.array(OrgGroupSchema),
});
export type BoardResponse = z.infer<typeof BoardResponseSchema>;

/** The board tabs: `all` shows every section; the others reuse the per-feature views. */
export const BoardTabSchema = z.enum(['all', 'issues', 'pull-requests']);
export type BoardTab = z.infer<typeof BoardTabSchema>;

/** Empty buckets, for a not-connected response or a missing provider. */
export function emptyBoardBuckets(): BoardBuckets {
  return {
    authored: [],
    delegated: [],
    assigned: [],
    reviewRequested: [],
    mentioned: [],
    reviewed: [],
    recentlyMerged: [],
  };
}

// An unset Linear priority (0/null) sorts last; 1 (urgent) sorts first.
function priorityRank(priority: number | null): number {
  return priority === null || priority === 0 ? 5 : priority;
}

/**
 * Prioritised order within a section: by Linear priority (urgent first; unset
 * last), then most recently updated. Pure + stable, so the dashboard and the
 * route present the same order.
 */
export function compareBoardItems(a: BoardItem, b: BoardItem): number {
  const byPriority = priorityRank(a.priority) - priorityRank(b.priority);
  if (byPriority !== 0) return byPriority;
  // Most-recently-updated first; `updatedAt` is a schema-enforced ISO timestamp.
  return Date.parse(b.updatedAt) - Date.parse(a.updatedAt);
}

/**
 * The canonical cache key for a board's people-filter scope. The route (which
 * writes the per-viewer `board_snapshots` cache) and the client (which reads it)
 * both derive it from their people list, so they agree on the key. Order- and
 * case-insensitive and deduped; the default "my work" view — `[]` or exactly
 * `['@me']` — is the empty key (both fetch the same `@me` board).
 */
export function boardScopeKey(people: readonly string[]): string {
  const canonical = [
    ...new Set(
      people.map((person) => person.trim().toLowerCase()).filter(Boolean),
    ),
  ].toSorted();
  if (
    canonical.length === 0 ||
    (canonical.length === 1 && canonical[0] === '@me')
  ) {
    return '';
  }
  return canonical.join(',');
}

/**
 * The canonical snapshot/request key for an explicit Me/Everyone/selected scope.
 * Everyone needs its own key: an empty legacy people list means Me, so sharing the
 * empty key would let the two top-nav scopes paint each other's cached board.
 */
export function boardPeopleScopeKey(scope: WorkItemsPeopleScope): string {
  switch (scope.kind) {
    case 'me': {
      return '';
    }
    case 'everyone': {
      return '@everyone';
    }
    case 'selected': {
      return scope.people.length === 0 ? '@none' : boardScopeKey(scope.people);
    }
    default: {
      return '';
    }
  }
}
